import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../context/authContext";
import Home from "../pages/Home";
import About from "../pages/About";
import AboutLuxury from "../pages/AboutLuxury";
import Product from "../pages/ProductList";
import ProductDetails from "../pages/ProductDetails";
import Contact from "../pages/Contact";
import Blog from "../pages/Blog";
import BlogDetails from "../pages/BlogDetails";
import NotFound from "../pages/NotFound";
import Login from "../components/auth/Login";
import Register from "../components/auth/Register";
import PaymentHistory from "../pages/PaymentHistory";
import Cart from "../pages/Cart";
import Checkout from "../pages/Checkout";
import OrderSuccess from "../pages/OrderSuccess";
import OrderDetails from "../pages/OrderDetails";
import DeliveryRoutePage from "../pages/Admin/Delivery/DeliveryRoutePage";
import NewDeliveriesPage from "../pages/Admin/Delivery/NewDeliveriesPage";
import DeliveryAdminDashboard from "../pages/Admin/Delivery/DeliveryAdminDashboard";
import Profile from '../pages/Profile';
import { Helmet } from "react-helmet-async";
// Admin Pages
import AdminLayout from "../components/Admin/AdminLayout";
import Dashboard from "../pages/Admin/Dashboard";
import Orders from "../pages/Admin/Orders/OrderList";
import Payments from "../pages/Admin/Payment/PaymentList";
import Paymentall from "../pages/Admin/Payment/PaymenManage";
import Blogs from "../pages/Admin/Blog/BlogList";
import AddBlog from "../pages/Admin/Blog/AddBlog";
import EditBlog from "../pages/Admin/Blog/EditBlog";
import ProductAdd from "../pages/Admin/Product/AddProduct";
import Users from "../pages/Admin/User/UserList";
import Productmanagment from "../pages/Admin/Product/Productmanagment";
import EditProduct from "../pages/Admin/Product/EditProduct";
import DesignGallery from "../pages/DesignGallery";
import DesignRequest from "../pages/DesignRequest";
import DesignList from "../pages/Admin/Design/DesignList";
import AddDesign from "../pages/Admin/Design/AddDesign";
import EditDesign from "../pages/Admin/Design/EditDesign";
import RequestList from "../pages/Admin/Design/RequestList";
import Terms from "../pages/Terms";
import Privacy from "../pages/Privacy";
import GenerateImage from '../pages/GenerateImage';
import GalleryPage from '../pages/GalleryPage';
import RohaCourse from "../pages/RohaCourse";
import GeneratedImages from '../pages/Admin/GeneratedImages';
import SendCampaign from '../pages/Admin/SendCampaign';
import SubscribeManagement from '../pages/Admin/SubscribeManagement';
import InventoryManagement from '../pages/Admin/InventoryManagement';
import GemstoneScanner from '../pages/GemstoneScanner'; // eslint-disable-line no-unused-vars

// ✅ Protect Admin and Delivery Admin Routes
const AdminRoute = () => {
  const { user, loading } = useContext(AuthContext);
  if (loading) return <div className="text-center my-5"><span>Loading...</span></div>;
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  const isAdmin = user.role === "admin" || user.role === "Admin";
  const isDelivery = user.role === "delivery" || user.role === "delivery_guy";
  // Allow admin and delivery roles for delivery admin dashboard
  if (window.location.pathname.startsWith("/admin/delivery") && (isAdmin || isDelivery)) {
    return <Outlet />;
  }
  // For all other admin routes, only allow admin
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
};

// ✅ Protect Delivery User or Admin Routes (for delivery/route and delivery/new)
const DeliveryUserOrAdminRoute = () => {
  const { user, loading } = useContext(AuthContext);
  if (loading) return <div className="text-center my-5"><span>Loading...</span></div>;
  if (!user) return <Navigate to="/login" replace />;
  const isDelivery = user.role === "delivery" || user.role === "delivery_guy";
  const isAdmin = user.role === "admin" || user.role === "Admin";
  return (isDelivery || isAdmin) ? <Outlet /> : <Navigate to="/" replace />;
};

// ✅ Protect User Routes
const ProtectedRoute = () => {
  const { user, loading } = useContext(AuthContext);
  if (loading) return <div className="text-center my-5"><span>Loading...</span></div>;
  return user ? <Outlet /> : <Navigate to="/login" replace />;
};

function TermsModal({ onAccept }) {
  const [show, setShow] = useState(true);
  useEffect(() => {
    const accepted = localStorage.getItem("roha_terms_accepted");
    if (accepted !== "true") {
      setShow(true);
    } else {
      setShow(false);
    }
  }, []);
  const handleAccept = () => {
    localStorage.setItem("roha_terms_accepted", "true");
    setShow(false);
    if (onAccept) onAccept();
  };
  if (!show) return null;
  // Block all interaction with a full overlay
  return (
    <div style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'rgba(10,22,51,0.98)',zIndex:99999,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{background:'#fff',borderRadius:16,padding:32,maxWidth:420,boxShadow:'0 8px 32px #0002',textAlign:'center'}}>
        <h2 style={{marginBottom:16}}>Welcome to Roha Jewellery</h2>
        <p style={{marginBottom:16}}>Before using our website, please review and accept our <a href="/terms" target="_blank" rel="noopener noreferrer">Terms & Conditions</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</p>
        <button className="btn btn-warning px-4" onClick={handleAccept}>I Accept</button>
      </div>
    </div>
  );
}

function Routers() {
  const [accepted, setAccepted] = useState(!!localStorage.getItem("roha_terms_accepted"));
  useEffect(() => {
    const checkAccepted = () => setAccepted(localStorage.getItem("roha_terms_accepted") === "true");
    window.addEventListener('storage', checkAccepted);
    checkAccepted();
    return () => window.removeEventListener('storage', checkAccepted);
  }, []);
  return (
    <>
      {!accepted && <TermsModal onAccept={() => setAccepted(true)} />}
    <Routes>
      {/* 🌍 Public Routes with SEO Helmet */}
      <Route path="/" element={
        <>
          <Helmet>
            <title>Roha Jewellery | Ethiopian Opal & Gemstone Jewellery</title>
            <meta name="description" content="Discover luxury Ethiopian opal, gemstone rings, necklaces, and gold jewellery. Handcrafted, certified, and delivered worldwide. Shop Ethiopia's finest gems online." />
            <link rel="canonical" href="https://rohajewellery.com/" />
            <meta name="keywords" content="Ethiopian opal, Ethiopian gemstone, gemstone jewellery, gold jewellery, luxury jewellery, Ethiopia, opal rings, opal necklaces, buy opal Ethiopia, Roha Jewellery" />
          </Helmet>
          <Home />
        </>
      } />
      <Route path="/about" element={
        <>
          <Helmet>
            <title>About Roha Jewellery | Ethiopia's Premier Gemstone House</title>
            <meta name="description" content="Learn about Roha Jewellery, Ethiopia's trusted source for opal, gemstone, and gold jewellery. Discover our story, values, and expert team." />
            <link rel="canonical" href="https://rohajewellery.com/about" />
            <meta name="keywords" content="About Roha Jewellery, Ethiopian opal, gemstone house, luxury jewellery Ethiopia, Ethiopian gemstones" />
          </Helmet>
          <AboutLuxury />
        </>
      } />
      <Route path="/products" element={
        <>
          <Helmet>
            <title>Shop Ethiopian Opal & Gemstone Jewellery | Roha Jewellery</title>
            <meta name="description" content="Browse our collection of Ethiopian opal, gemstone rings, necklaces, and gold jewellery. Handcrafted and ethically sourced in Ethiopia." />
            <link rel="canonical" href="https://rohajewellery.com/products" />
            <meta name="keywords" content="Ethiopian opal, Ethiopian gemstone, gemstone rings, gemstone necklaces, gold jewellery, buy opal Ethiopia, Roha Jewellery" />
          </Helmet>
          <Product />
        </>
      } />
      <Route path="/products/:id" element={
        <>
          <Helmet>
            <title>Product Details | Ethiopian Opal & Gemstone | Roha Jewellery</title>
            <meta name="description" content="View details for this luxury Ethiopian opal or gemstone jewellery piece. Certified, handcrafted, and available for worldwide delivery." />
            <meta name="keywords" content="Ethiopian opal, gemstone details, opal ring, opal necklace, buy opal Ethiopia, Roha Jewellery" />
          </Helmet>
          <ProductDetails />
        </>
      } />
      <Route path="/contact" element={
        <>
          <Helmet>
            <title>Contact Roha Jewellery | Ethiopian Opal & Gemstone Experts</title>
            <meta name="description" content="Contact Roha Jewellery for inquiries about Ethiopian opal, gemstones, gold jewellery, and custom orders. We're here to help you find the perfect piece." />
            <link rel="canonical" href="https://rohajewellery.com/contact" />
            <meta name="keywords" content="Contact Roha Jewellery, Ethiopian opal, gemstone experts, luxury jewellery Ethiopia" />
          </Helmet>
          <Contact />
        </>
      } />
      <Route path="/blogs" element={
        <>
          <Helmet>
            <title>Jewellery Blog | Ethiopian Opal, Gemstone & Gold News</title>
            <meta name="description" content="Read the latest on Ethiopian opal, gemstone trends, gold jewellery, and Roha Jewellery news. Expert advice and inspiration for jewellery lovers." />
            <link rel="canonical" href="https://rohajewellery.com/blogs" />
            <meta name="keywords" content="Ethiopian opal blog, gemstone news, gold jewellery blog, Roha Jewellery blog, Ethiopia opal" />
          </Helmet>
          <Blog />
        </>
      } />
      <Route path="/blogs/:id" element={
        <>
          <Helmet>
            <title>Blog Details | Ethiopian Opal & Gemstone Stories</title>
            <meta name="description" content="Read this story about Ethiopian opal, gemstones, and luxury jewellery from Roha Jewellery. Inspiration, advice, and expert insights." />
            <meta name="keywords" content="Ethiopian opal, gemstone blog, jewellery stories, Roha Jewellery" />
          </Helmet>
          <BlogDetails />
        </>
      } />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/cart" element={
        <>
          <Helmet>
            <title>Your Cart | Roha Jewellery | Ethiopian Opal & Gemstone</title>
            <meta name="description" content="View your cart and shop Ethiopian opal, gemstone, and gold jewellery. Secure checkout and worldwide delivery from Roha Jewellery." />
            <link rel="canonical" href="https://rohajewellery.com/cart" />
            <meta name="keywords" content="Cart, buy Ethiopian opal, gemstone cart, gold jewellery cart, Roha Jewellery" />
          </Helmet>
          <Cart />
        </>
      } />
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/payment/success/:orderId" element={<OrderSuccess />} />
      <Route path="/orders/:orderId" element={<OrderDetails />} />
      <Route path="/my-profile" element={<Profile />} />
      <Route path="/course" element={<RohaCourse />} />
      <Route path="/designs" element={<DesignGallery />} />
      {/* Design Request (protected for customers) */}
      <Route element={<ProtectedRoute />}>
        <Route path="/design-request" element={<DesignRequest />} />
      </Route>
      {/* 🔒 Protected User Routes */}
      <Route element={<ProtectedRoute />}>
        <Route path="/payment-history" element={<PaymentHistory />} />
      </Route>
      {/* 🔒 Delivery User or Admin Routes */}
      <Route element={<DeliveryUserOrAdminRoute />}>
        <Route path="/delivery/route" element={<DeliveryRoutePage />} />
        <Route path="/delivery/new" element={<NewDeliveriesPage />} />
      </Route>
      {/* 🔒 Delivery Admin Route (admin or delivery roles) */}
      <Route element={<AdminRoute />}>
        <Route element={<AdminLayout />}>
          <Route path="/delivery/admin" element={<DeliveryAdminDashboard />} />
        </Route>
      </Route>
      {/* 🔐 Admin Routes (Nested Protection) */}
      <Route element={<AdminRoute />}>
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="products" element={<Productmanagment />} />
          <Route path="products/add" element={<ProductAdd />} />
          <Route path="orders" element={<Orders />} />
          <Route path="payments" element={<Payments />} />
          <Route path="payall" element={<Paymentall />} />
          <Route path="blogs" element={<Blogs />} />
          <Route path="blogs/add" element={<AddBlog />} />
          <Route path="blogs/edit/:id" element={<EditBlog />} />
          <Route path="users" element={<Users />} />
          <Route path="/admin/products/edit/:id" element={<EditProduct />} />
          <Route path="design" element={<DesignList />} />
          <Route path="design/add" element={<AddDesign />} />
          <Route path="inventory" element={<InventoryManagement />} />
          <Route path="design/edit/:id" element={<EditDesign />} />
          <Route path="design/requests" element={<RequestList />} />
          <Route path="generated-images" element={<GeneratedImages />} />
          <Route path="send-campaign" element={<SendCampaign />} />
          <Route path="subscribe-management" element={<SubscribeManagement />} />
        </Route>
      </Route>
      {/* 🚫 Not Found Route */}
      <Route path="*" element={<NotFound />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
      <Route path="/generate-image" element={<GenerateImage />} />
      <Route path="/ai-gallery" element={<GalleryPage />} />
      {/* <Route path="/gemstone-scanner" element={<GemstoneScanner />} /> */}
      <Route path="/test-add-blog" element={<AddBlog />} />
    </Routes>
    </>
  );  
}

export default Routers;
